import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationCircleIcon,
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  HomeIcon,
  AcademicCapIcon,
  CreditCardIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const RelocationJourney = ({ clientId }) => {
  const [activePhase, setActivePhase] = useState('preparation');

  const journeyPhases = [
    {
      id: 'preparation',
      title: 'Preparation',
      description: 'Getting ready for your move',
      icon: DocumentTextIcon,
      status: 'completed',
      color: 'bg-green-500',
      milestones: [
        { title: 'Visa Application', status: 'completed', date: '2024-01-15' },
        { title: 'Document Preparation', status: 'completed', date: '2024-01-20' },
        { title: 'Travel Insurance', status: 'completed', date: '2024-01-25' }
      ]
    },
    {
      id: 'arrival',
      title: 'Arrival & Setup',
      description: 'First steps in Spain',
      icon: MapPinIcon,
      status: 'in-progress',
      color: 'bg-blue-500',
      milestones: [
        { title: 'Airport Pickup', status: 'completed', date: '2024-02-01' },
        { title: 'Temporary Accommodation', status: 'completed', date: '2024-02-01' },
        { title: 'NIE Application', status: 'in-progress', dueDate: '2024-02-15' },
        { title: 'Bank Account Opening', status: 'pending', dueDate: '2024-02-20' }
      ]
    },
    {
      id: 'settlement',
      title: 'Settlement',
      description: 'Building your new life',
      icon: HomeIcon,
      status: 'pending',
      color: 'bg-purple-500',
      milestones: [
        { title: 'Housing Search', status: 'pending', dueDate: '2024-02-25' },
        { title: 'Empadronamiento', status: 'pending', dueDate: '2024-03-01' },
        { title: 'TIE Card Application', status: 'pending', dueDate: '2024-03-05' },
        { title: 'Utilities Setup', status: 'pending', dueDate: '2024-03-10' }
      ]
    },
    {
      id: 'integration',
      title: 'Integration',
      description: 'Becoming part of the community',
      icon: AcademicCapIcon,
      status: 'pending',
      color: 'bg-orange-500',
      milestones: [
        { title: 'School Enrollment', status: 'pending', dueDate: '2024-03-15' },
        { title: 'Spanish Classes', status: 'pending', dueDate: '2024-03-20' },
        { title: 'Healthcare Registration', status: 'pending', dueDate: '2024-03-25' },
        { title: 'Driver\'s License', status: 'pending', dueDate: '2024-04-01' }
      ]
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'in-progress':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'pending':
        return <ExclamationCircleIcon className="h-5 w-5 text-muted-foreground" />;
      default:
        return <ClockIcon className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-muted text-foreground';
      default: return 'bg-muted text-foreground';
    }
  };

  const progressPercentage = 35; // Calculate based on completed milestones

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 dark:from-blue-950/10 to-purple-50 dark:to-purple-950/10 border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">Your Relocation Journey</h2>
            <p className="text-muted-foreground">Track your progress as you settle into Spain</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">{progressPercentage}%</div>
            <div className="text-sm text-muted-foreground">Complete</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-muted rounded-full h-3 mb-4">
          <div 
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Started Feb 1, 2024</span>
          <span>Estimated completion: Apr 15, 2024</span>
        </div>
      </Card>

      {/* Journey Phases */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {journeyPhases.map((phase) => {
          const Icon = phase.icon;
          const isActive = phase.id === activePhase;
          
          return (
            <Card 
              key={phase.id}
              className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                isActive ? 'ring-2 ring-blue-500 bg-blue-50' : ''
              }`}
              onClick={() => setActivePhase(phase.id)}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className={`p-2 rounded-lg ${phase.color}`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">{phase.title}</h3>
                  <p className="text-xs text-muted-foreground">{phase.description}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                {phase.milestones.slice(0, 2).map((milestone, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-foreground">{milestone.title}</span>
                    {getStatusIcon(milestone.status)}
                  </div>
                ))}
                {phase.milestones.length > 2 && (
                  <div className="text-xs text-muted-foreground">
                    +{phase.milestones.length - 2} more tasks
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Detailed Phase View */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          {(() => {
            const phase = journeyPhases.find(p => p.id === activePhase);
            const Icon = phase.icon;
            return (
              <>
                <div className={`p-3 rounded-lg ${phase.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{phase.title}</h3>
                  <p className="text-muted-foreground">{phase.description}</p>
                </div>
              </>
            );
          })()}
        </div>

        <div className="space-y-4">
          {journeyPhases.find(p => p.id === activePhase)?.milestones.map((milestone, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                {getStatusIcon(milestone.status)}
                <div>
                  <div className="font-medium text-foreground">{milestone.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {milestone.status === 'completed' && milestone.date && (
                      <>Completed on {new Date(milestone.date).toLocaleDateString()}</>
                    )}
                    {milestone.status === 'in-progress' && milestone.dueDate && (
                      <>Due {new Date(milestone.dueDate).toLocaleDateString()}</>
                    )}
                    {milestone.status === 'pending' && milestone.dueDate && (
                      <>Due {new Date(milestone.dueDate).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Badge className={getStatusColor(milestone.status)}>
                  {milestone.status}
                </Badge>
                {milestone.status === 'in-progress' && (
                  <Button size="sm" variant="outline">
                    Continue
                  </Button>
                )}
                {milestone.status === 'pending' && (
                  <Button size="sm" variant="outline">
                    Start
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* AI Assistant */}
      <Card className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-500 p-2 rounded-lg">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Need Help?</h3>
              <p className="text-sm text-muted-foreground">Ask our AI assistant about your next steps</p>
            </div>
          </div>
          <Button className="bg-purple-600 hover:bg-purple-700">
            Get Help
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default RelocationJourney;

